import { BlogPost } from '@/types/blog';
import SEO from '@/components/SEO';
import { 
  extractEntities, 
  extractTopics, 
  createArticleSchema, 
  createBreadcrumbSchema, 
  createAuthorSchema 
} from '@/utils/schemaUtils';

interface BlogSEOProps {
  post: BlogPost;
}

const BlogSEO = ({ post }: BlogSEOProps) => {
  // Extract AI-optimized metadata from content
  const entities = extractEntities(post.content);
  const topics = extractTopics(post.content);
  
  // Extract list items for ItemList schema (from "Tools mentioned in this guide")
  const extractItemListFromContent = (markdown: string): { name: string; url: string }[] => {
    const section = markdown.split('### Tools mentioned in this guide')[1] || '';
    const regex = /-\s+\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
    const items: { name: string; url: string }[] = [];
    let match: RegExpExecArray | null;
    while ((match = regex.exec(section)) !== null) {
      items.push({ name: match[1], url: match[2] });
    }
    return items;
  };
  const itemListItems = extractItemListFromContent(post.content);
  
  // Create comprehensive structured data (conditionally include FAQ schema)
  const faqSchema = post.faq && post.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faq.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  } : null;

  // ItemList schema for listicle visibility in SERP and AI overviews
  const itemListSchema = itemListItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": post.title,
    "description": post.description,
    "numberOfItems": itemListItems.length,
    "itemListElement": itemListItems.map((it, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": it.name,
      "url": it.url
    }))
  } : null;

  const structuredData = [
    createArticleSchema(post),
    createBreadcrumbSchema(post),
    createAuthorSchema(post),
    ...(faqSchema ? [faqSchema] : []),
    ...(itemListSchema ? [itemListSchema] : [])
  ];

  // Generate AI-optimized title and description
  const seoTitle = post.seo?.title || `${post.title} | CodeRide Blog`;
  const seoDescription = post.seo?.description || post.description;
  
  // Combine SEO keywords with extracted entities
  const keywords = [
    ...(post.seo?.keywords || []),
    ...(post.tags || []),
    'CodeRide',
    'AI development',
    'code assistant'
  ];

  return (
    <SEO
      title={seoTitle}
      description={seoDescription}
      canonical={`https://coderide.ai/blog/${post.slug}`}
      type="article"
      
      // AI Search Engine Optimization
      keywords={keywords}
      entities={entities}
      topics={topics}
      
      // Article-specific metadata
      publishedTime={post.date}
      modifiedTime={post.modifiedTime}
      author={post.author}
      section="Technology"
      tags={post.tags}
      
      // Rich media
      image={post.image}
      imageAlt={post.imageAlt || post.title}
      
      // Structured data
      structuredData={structuredData}
    >
      {/* Additional article-specific meta tags */}
      <meta name="article:publisher" content="https://coderide.ai" />
      <meta name="article:author" content={post.author} />
      {post.tags && post.tags.map(tag => (
        <meta key={`keyword-${tag}`} name="news_keywords" content={tag} />
      ))}
      
      {/* AI-specific meta tags */}
      <meta name="content-category" content="Technology" />
      <meta name="content-type" content="article" />
      <meta name="audience" content="developers" />
      <meta name="target-audience" content="software developers, AI enthusiasts" />
      
      {/* Enhanced indexing directives */}
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Social media optimization */}
      <meta property="article:publisher" content="https://coderide.ai" />
      <meta property="og:article:author" content={post.author} />
      <meta property="og:article:section" content="Technology" />
      
      {/* Twitter-specific enhancements */}
      <meta name="twitter:label1" content="Reading time" />
      <meta name="twitter:data1" content={`${Math.ceil(post.content.split(' ').length / 200)} min read`} />
      <meta name="twitter:label2" content="Filed under" />
      <meta name="twitter:data2" content={post.tags?.join(', ') || 'Technology'} />
    </SEO>
  );
};

export default BlogSEO;
